import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { FullLicenseCard } from "../components/LicenseCard";

type Details = {
  firstName: string;
  lastName: string;
  identificationNumber: string;
  expirationDate: string;
  country?: string;
};

type PilotProfile = {
  name: string;
  pilotId: string;
  operatorId: string;
  licenseA1A3: boolean;
  licenseA2: boolean;
  licenseSTS: boolean;
  licenseA1A3Details: Details | null;
  licenseA2Details: Details | null;
  photoUrl: string | null;
};

type Tier = "A2" | "A1A3";

/**
 * Eine Lizenz gilt als vollständig ausgefüllt, wenn Vor-/Nachname,
 * Nachweis-Nr. und Ablaufdatum hinterlegt sind — analog zur App-Logik
 * (isLicenseComplete) in der nativen Lizenzkarten-Ansicht.
 */
function isLicenseComplete(details: Details | null | undefined): boolean {
  if (!details) return false;
  return (
    details.firstName.trim().length > 0 &&
    details.lastName.trim().length > 0 &&
    details.identificationNumber.trim().length > 0 &&
    details.expirationDate.trim().length > 0
  );
}

/**
 * Listet alle vom Piloten vollständig ausgefüllten Lizenzen — höchste zuerst
 * (A2 vor A1/A3), genau wie die native Lizenzkarten-Ansicht.
 */
function getAvailableTiers(profile: PilotProfile): Array<{ tier: Tier; label: string }> {
  const list: Array<{ tier: Tier; label: string }> = [];
  if (profile.licenseA2 && isLicenseComplete(profile.licenseA2Details)) {
    list.push({ tier: "A2", label: "Fernpiloten-Zeugnis A2" });
  }
  if (profile.licenseA1A3 && isLicenseComplete(profile.licenseA1A3Details)) {
    list.push({ tier: "A1A3", label: "EU-Kompetenznachweis A1/A3" });
  }
  return list;
}

export default function LicenseCardPage() {
  const { data, isLoading } = useQuery<PilotProfile | null>({ queryKey: ["/api/profile"] });

  if (isLoading) {
    return (
      <div className="text-center text-[var(--color-app-text-secondary)] py-10" data-testid="loading-license-card">
        Lizenzkarten werden geladen …
      </div>
    );
  }

  const tiers = data ? getAvailableTiers(data) : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-[var(--color-app-text)]">Piloten-Lizenzkarte</h1>
        <p className="text-sm text-[var(--color-app-text-secondary)] mt-1">
          {tiers.length === 0
            ? "Lizenzkarten zum Vorzeigen bei einer Kontrolle."
            : tiers.length === 1
              ? "Eine vollständig ausgefüllte Lizenz — zum Vorzeigen verfügbar."
              : `${tiers.length} vollständig ausgefüllte Lizenzen — alle zum Vorzeigen verfügbar.`}
        </p>
      </div>

      {tiers.length === 0 ? (
        <div className="card text-center text-[var(--color-app-text-secondary)] py-10 space-y-3" data-testid="empty-license-card">
          <p>
            Keine vollständig ausgefüllte Lizenz vorhanden. Bitte ergänze die
            Lizenzdetails im Pilotenprofil.
          </p>
          <Link to="/profil" className="btn-secondary inline-block" data-testid="link-to-profile">
            Zum Pilotenprofil
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {tiers.map((item) => (
            <div key={item.tier} className="card space-y-3" data-testid={`section-license-${item.tier}`}>
              <h2 className="font-semibold text-[var(--color-app-text)]">{item.label}</h2>
              <FullLicenseCard
                tier={item.tier}
                pilotName={data!.name}
                pilotId={data!.pilotId}
                details={item.tier === "A2" ? data!.licenseA2Details : data!.licenseA1A3Details}
                photoUrl={data!.photoUrl}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
